import React, { useState, useEffect } from 'react';
import { CheckCircle2, Circle, Zap, Loader2, Trophy, Flame, Gift, Star } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { pointService } from '../../services/pointService';
import { useToast } from '../../context/ToastContext';
import { t } from '../../i18n';

interface AttendanceViewProps {
  userId: string;
  lang: 'ko' | 'en';
  onPointsUpdate?: (newPoints: number) => void;
}

export const AttendanceView: React.FC<AttendanceViewProps> = ({ userId, lang, onPointsUpdate }) => {
  const { showToast } = useToast();
  const [status, setStatus] = useState<{ attended: boolean, streak: number }>({ attended: false, streak: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    loadStatus();
  }, [userId]);

  const loadStatus = async () => {
    setIsLoading(true);
    try {
      const data = await pointService.checkAttendance(userId);
      setStatus(data);
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAttendance = async () => {
    if (status.attended || isProcessing) return;
    
    setIsProcessing(true);
    try {
      const result = await pointService.recordAttendance(userId);
      if (result.success) {
        setStatus({ attended: true, streak: result.streak });
        showToast('success', `${t(lang, 'attendanceComplete')} +${result.pointsEarned}P`);
        
        // 유저 포인트 새로고침
        const newPoints = await pointService.getUserPoints(userId);
        if (onPointsUpdate) onPointsUpdate(newPoints);
      }
    } catch (e) {
      console.error(e);
      showToast('error', t(lang, 'errorProcessingAttendance'));
    } finally {
      setIsProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-rose-500" size={32} />
      </div>
    );
  }

  return (
    <div className="p-4 pb-40 sm:p-6 space-y-6">
      {/* Streak Hero Card */}
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden bg-slate-900 rounded-[32px] p-8 text-white shadow-2xl group"
      >
        {/* Animated Background Gradients */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-rose-500/20 blur-[100px] rounded-full -translate-y-1/2 translate-x-1/2 md:group-hover:bg-rose-500/30 transition-colors duration-700"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 blur-[80px] rounded-full translate-y-1/2 -translate-x-1/4"></div>

        <div className="relative z-10">
          <div className="flex justify-between items-start mb-8">
            <div className="space-y-1">
              <h3 className="text-2xl font-black tracking-tight">
                {t(lang, 'attendanceStreak')}
              </h3>
              <p className="text-slate-400 text-sm font-medium">
                {t(lang, 'attendanceNote')}
              </p>
            </div>
            <motion.div 
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 4, repeat: Infinity }}
              className="p-3 bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10"
            >
              <Flame className="text-orange-500 fill-orange-500" size={24} />
            </motion.div>
          </div>

          <div className="flex items-baseline gap-3 mb-8">
            <motion.span 
              key={status.streak}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-7xl px-1 font-black tracking-tighter bg-gradient-to-b from-white to-slate-400 bg-clip-text text-transparent"
            >
              {status.streak}
            </motion.span>
            <span className="text-xl font-black text-rose-500 uppercase tracking-widest italic">{lang === 'ko' ? '일' : 'Days'}</span>
          </div>

          <motion.button 
            whileHover={!status.attended && !isProcessing ? { scale: 1.02 } : {}}
            whileTap={!status.attended && !isProcessing ? { scale: 0.98 } : {}}
            onClick={handleAttendance}
            disabled={status.attended || isProcessing}
            className={`w-full py-5 rounded-2xl font-black text-sm uppercase tracking-widest transition-all relative overflow-hidden flex items-center justify-center gap-3 ${
              status.attended 
                ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' 
                : 'bg-white text-slate-900 shadow-xl shadow-white/10'
            }`}
          >
            {isProcessing ? (
              <Loader2 className="animate-spin" size={20} />
            ) : status.attended ? (
              <>
                <CheckCircle2 size={20} />
                {t(lang, 'attendanceCompleted')}
              </>
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-r from-rose-500 to-rose-600 opacity-0 md:hover:opacity-100 transition-opacity"></div>
                <span className="relative z-10 flex items-center gap-3">
                  <Zap size={20} className="fill-current" />
                  {t(lang, 'checkAttendanceNow')}
                </span>
              </>
            )}
          </motion.button>
        </div>
      </motion.div>

      {/* Progress Track */}
      <div className="space-y-4">
        <div className="flex justify-between items-center px-1">
          <h4 className="text-slate-900 font-black text-sm uppercase tracking-widest flex items-center gap-2">
            <Star size={16} className="text-rose-500 fill-rose-500" />
            {t(lang, 'weeklyProgress')}
          </h4>
          <span className="text-xs font-bold text-slate-400">
            {Math.floor(status.streak / 7) + 1}{lang === 'ko' ? '주차 진행 중' : ' Week Progress'}
          </span>
        </div>

        <div className="grid grid-cols-7 gap-2 sm:gap-3">
          {[1, 2, 3, 4, 5, 6, 7].map((day) => {
            // Calculate progress based on cumulative attendance (streak field now holds total count)
            const currentDayInCycle = status.streak % 7 === 0 && status.streak > 0 ? 7 : status.streak % 7;
            
            // If user attended today, they have completed 'currentDayInCycle'
            // If they haven't attended today, they have completed 'currentDayInCycle' (which belongs to previous attendance)
            // But we want to show the status relative to the 7-day cycle.
            const isCompleted = day <= currentDayInCycle;
            const isNextToAttend = !status.attended && (
              (currentDayInCycle === 7 && day === 1) || 
              (currentDayInCycle < 7 && day === currentDayInCycle + 1)
            );

            return (
              <motion.div 
                key={day}
                whileHover={{ y: -2 }}
                className="flex flex-col items-center gap-2"
              >
                <div className={`w-full aspect-square rounded-2xl flex items-center justify-center border-2 transition-all relative ${
                  isCompleted
                    ? 'bg-rose-500 border-rose-500 text-white shadow-lg shadow-rose-500/20' 
                    : isNextToAttend
                    ? 'bg-white border-rose-500 border-dashed text-rose-500 animate-pulse'
                    : 'bg-slate-50 border-slate-100 text-slate-300'
                }`}>
                  {isCompleted ? (
                    <CheckCircle2 size={24} className="stroke-[3]" />
                  ) : day === 7 ? (
                    <Trophy size={20} />
                  ) : (
                    <span className="text-sm font-black">{day}</span>
                  )}
                  
                  {day === 7 && (
                    <div className="absolute -top-1 -right-1">
                      <Gift size={14} className={isCompleted ? 'text-white' : 'text-rose-500'} />
                    </div>
                  )}
                </div>
                <span className={`text-[10px] font-black uppercase tracking-tighter ${
                  isCompleted ? 'text-rose-500' : 'text-slate-400'
                }`}>
                  {lang === 'ko' ? `${day}일차` : `Day ${day}`}
                </span>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Rewards Info Section */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-slate-50 rounded-[24px] p-5 border border-slate-100 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm border border-slate-100">
            <Zap size={20} className="text-blue-500" />
          </div>
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">
              {t(lang, 'baseReward')}
            </p>
            <p className="text-sm font-black text-slate-900">+10 Points / {lang === 'ko' ? '일' : 'Day'}</p>
          </div>
        </div>

        <div className="bg-rose-50/50 rounded-[24px] p-5 border border-rose-100/50 flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center shadow-sm border border-rose-100/50">
            <Trophy size={20} className="text-rose-500" />
          </div>
          <div>
            <p className="text-[10px] font-black text-rose-500/60 uppercase tracking-widest mb-0.5">
              {t(lang, 'streakBonus')}
            </p>
            <p className="text-sm font-black text-slate-900">+50 Points / {lang === 'ko' ? '7일' : '7 Days'}</p>
          </div>
        </div>
      </div>

    </div>
  );
};
